import { MessageProvider } from "@messageformat/react";
import { Link } from "gatsby";
import * as React from "react";
import { createPortal } from "react-dom";
import {
  hasAdTrafficUrlParams,
  setMarketingConsent,
  shouldShowConsentBanner,
  touchAdVisitor,
} from "../analytics/googleAds";
import { useTranslator } from "../app/utils/translator";
import { Button } from "../catalystComponents/button";
import { consentMessages } from "../translations";
import { Lang, detectLangFromPath } from "../types";
import { urls } from "../urls";

/**
 * Inner component that uses the translation context.
 * This is separated so it can be wrapped in MessageProvider.
 */
function MarketingConsentBannerInner({ lang }: { lang: Lang }) {
  const [showBanner, setShowBanner] = React.useState(false);
  const t = useTranslator("consent");

  React.useEffect(() => {
    // Mark as ad visitor if coming from ad
    if (hasAdTrafficUrlParams()) {
      touchAdVisitor();
    }
    // Check if we should show the banner (runs on client only)
    if (shouldShowConsentBanner()) {
      setShowBanner(true);
    }
  }, []);

  const handleAccept = () => {
    setMarketingConsent("granted");
    setShowBanner(false);
  };

  const handleDecline = () => {
    setMarketingConsent("denied");
    setShowBanner(false);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <div
      data-test="consent-banner"
      className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4 sm:px-6 lg:px-8"
      role="dialog"
      aria-labelledby="consent-banner-title"
      aria-describedby="consent-banner-description"
    >
      <div className="mx-auto max-w-xl rounded-xl bg-white p-4 shadow-2xl ring-1 ring-gray-900/10 sm:p-6 dark:bg-zinc-900 dark:ring-white/10">
        <h2
          id="consent-banner-title"
          className="text-base font-semibold text-gray-900 dark:text-white"
        >
          {t("title")}
        </h2>
        <p
          id="consent-banner-description"
          className="mt-2 text-sm text-gray-600 dark:text-gray-400"
        >
          {t("description")}{" "}
          <Link
            to={urls[lang].privacy}
            className="text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
          >
            {t("privacy-link")}
          </Link>
        </p>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:gap-3">
          <Button
            data-test="consent-accept-btn"
            color="indigo"
            onClick={handleAccept}
            className="flex-1 sm:order-2"
          >
            {t("accept")}
          </Button>
          <Button
            data-test="consent-decline-btn"
            plain
            onClick={handleDecline}
            className="flex-1 sm:order-1"
          >
            {t("decline")}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Marketing Consent Banner
 *
 * This banner is shown only to users arriving from Google Ads who haven't
 * yet made a consent decision. It allows them to accept or decline
 * marketing cookies/tracking.
 *
 * Note: This component provides its own MessageProvider because it renders
 * outside the page-level MessageProvider in gatsby-browser.tsx's wrapPageElement.
 */
export default function MarketingConsentBanner() {
  const [mounted, setMounted] = React.useState(false);
  const portalRoot = React.useRef<HTMLDivElement | null>(null);
  const [lang, setLang] = React.useState<Lang>("en");

  React.useEffect(() => {
    const div = document.createElement("div");
    div.id = "marketing-consent-banner-portal";
    document.body.appendChild(div);
    portalRoot.current = div;
    setMounted(true);
    return () => {
      document.body.removeChild(div);
    };
  }, []);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      setLang(detectLangFromPath(window.location.pathname));
    }
  }, []);

  if (!mounted || !portalRoot.current) return null;

  const marketingConsentBannerPortal = createPortal(
    <MarketingConsentBannerInner lang={lang} />,
    portalRoot.current,
  );

  return (
    <MessageProvider messages={consentMessages[lang]}>
      {marketingConsentBannerPortal}
    </MessageProvider>
  );
}
